import { createAsyncThunk } from '@reduxjs/toolkit';
import * as TeamServices from 'TeamModule/services';
import * as Types from 'TeamModule/types';
import { AppStateType } from 'reducers';
import omit from 'lodash/omit';
import { fetchCsmAssignments } from 'CsmModule/thunks/csm';
import ApiClientManager from 'core/apiClient';

export const fetchTeams = createAsyncThunk<Types.FetchTeamsSuccessResponse, Types.FetchTeamsParams>(
  'fetchTeams',
  async (
    {
      requestStatusId,
      onSuccess,
      onFailure,
      teamIds,
      limit,
      initial: _initial,
      skipCsmFetch,
      isChainingRequests: _isChainingRequests,
      isTakeLatest: _isTakeLatest, // should be in meta
      tenant,
      realmId,
      ...params
    }: Types.FetchTeamsParams,
    { dispatch, getState, rejectWithValue },
  ) => {
    try {
      const {
        authReducer: { user },
        csmReducer: { csmAssignmentByTeamHash },
      } = getState() as AppStateType;
      const auth_token = user?.auth_token;

      // Build custom headers if tenant/realmId are provided
      const customHeaders =
        tenant && realmId
          ? ApiClientManager.buildUrlHeaders({ tenantName: tenant, realmId })
          : undefined;

      const response = await TeamServices.getTeams(
        {
          params: {
            limit,
            ...(teamIds
              ? {
                  limit: teamIds.length,
                  ids: teamIds,
                }
              : null),
            ...params,
          },
          requestStatusId,
          ...(customHeaders && { headers: customHeaders }),
        },
        auth_token as string,
      );

      if (teamIds && !skipCsmFetch) {
        // Fetch csm assignments
        const teamIdsToFetchCsmAssignments = teamIds.reduce<number[]>((acc, teamId) => {
          if (!csmAssignmentByTeamHash[teamId]) acc.push(teamId);
          return acc;
        }, []);
        if (teamIdsToFetchCsmAssignments.length) {
          void dispatch(
            fetchCsmAssignments({
              mosaicTeamIds: teamIdsToFetchCsmAssignments,
            }),
          );
        }
      }

      if (onSuccess) {
        onSuccess({ response: response as any });
      }
      return response.data as any;
    } catch (e) {
      if (onFailure) {
        onFailure({ error: e });
      }
      return rejectWithValue(e);
    }
  },
);

export const createTeam = createAsyncThunk(
  'createTeam',
  async (
    { meta, tenant_name, realm_id, ...params }: Types.CreateTeamParams,
    { getState, rejectWithValue },
  ) => {
    const { onSuccess, onFailure } = meta || {};
    try {
      const {
        authReducer: { user },
      } = getState() as AppStateType;
      const auth_token = user?.auth_token;

      const customHeaders = tenant_name
        ? ApiClientManager.buildUrlHeaders({ tenantName: tenant_name, realmId: realm_id })
        : undefined;

      /**
       * Spec
       * On Prod:
       *  - If (subscription) realm id is selected > Show plus button + use default rails server url
       *  - If (subscription) realm id not selected > Use default rails server url
       * On Demo:
       *  - If realm id is selected > Hide + button
       *  - If realm id not selected > Use default rails server url
       */
      const response = await TeamServices.createTeam(
        { ...params, tenant_name, realm_id },
        customHeaders ? { headers: customHeaders } : {},
        auth_token as string,
      );
      if (onSuccess) {
        onSuccess({ response: response as any });
      }
      return response.data as any;
    } catch (e) {
      if (onFailure) {
        onFailure({ error: e });
      }
      return rejectWithValue(e);
    }
  },
);

export const deleteBatchTimeEntries = createAsyncThunk(
  'deleteBatchTimeEntries',
  async (
    params: Types.DeleteBatchTimeEntriesParams,
    { dispatch: _dispatch, getState, rejectWithValue },
  ) => {
    const { onSuccess, onFailure } = params;
    try {
      const {
        authReducer: { user },
      } = getState() as AppStateType;
      const auth_token = user?.auth_token;
      const body = omit(params, ['onFailure', 'onSuccess']);

      const response = await TeamServices.deleteBatchTimeEntries(
        {},
        body,
        {},
        auth_token as string,
      );

      if (onSuccess) {
        onSuccess({ response });
      }

      return response.data;
    } catch (e) {
      if (onFailure) {
        onFailure({ error: e });
      }
      return rejectWithValue(e);
    }
  },
);

export const exportDeletedWorkPlanCsv = createAsyncThunk(
  'exportDeletedWorkPlanCsv',
  async (
    params: Types.ExportDeletedWorkPlanCsvParams,
    { dispatch: _dispatch, getState, rejectWithValue },
  ) => {
    const { onSuccess, onFailure } = params;
    try {
      const {
        authReducer: { user },
      } = getState() as AppStateType;
      const auth_token = user?.auth_token;

      const response = await TeamServices.exportDeletedWorkPlanCsv(
        params,
        {},
        auth_token as string,
      );
      if (onSuccess) {
        onSuccess({ response });
      }
      return response;
    } catch (e) {
      if (onFailure) {
        onFailure({ error: e });
      }
      return rejectWithValue(e);
    }
  },
);
